import {
  formatFreeTilesLayout,
  parseFreeTilesLayout
} from 'common/buyOneGetManyLayout';
import { FreeTilesLayout, IOnePlusManyInstanceConfig } from 'common/contracts';
import {
  EBlockType,
  IOnePlusManyStoreBlock,
  TStoreBlock
} from 'common/TemplateStudio.types';
import {
  BodyBlockId,
  BodyBlockV2Item,
  buildOnePlusManyInstanceId,
  DEFAULT_BODY_BLOCKS_ORDER,
  getBaseBlockType,
  MERGEABLE_BODY_BLOCKS,
  OnePlusManyInstanceId
} from 'constants/bodyBlocks';
import { StoreBodyBlock } from 'constants/enums';
import { processRichTextContent } from 'utils/colorUtils';
import { v4 } from 'uuid';
import type { z } from 'zod';

import { EThemes } from 'components/TemplatesStudio/enums';

import { collapseOnePlusManyToSingleSlot } from '../bodyBlocksOrderV2.utils';
import type { templateStudioSchema } from '../Components/FormPage/validation-schema';
import {
  FIRST_INSTANCE_SUFFIX,
  parseOnePlusManyInstanceKeyFromWireId
} from '../multiInstanceBlocks/multiInstanceBlocks.consts';
import type {
  IFormStoreBlock,
  IFormStoreBlockStub,
  IOnePlusManyFormStoreBlock
} from '../multiInstanceBlocks/multiInstanceBlocks.types';

type BuildDefaultOnePlusManyInstance = (
  instanceKey: OnePlusManyInstanceId
) => IOnePlusManyFormStoreBlock;

const DEFAULT_ONE_PLUS_MANY_MAX_OFFERS = 10;

type TemplateStudioFormValues = z.infer<typeof templateStudioSchema>;

type BlockFields = Record<string, unknown>;

interface BlockBuilder {
  type: EBlockType;
  build: (values: TemplateStudioFormValues) => BlockFields;
}

export const E_BLOCK_TYPE_TO_STORE_BODY_BLOCK: Partial<
  Record<EBlockType, StoreBodyBlock>
> = {
  [EBlockType.BUNDLE]: StoreBodyBlock.bundles,
  [EBlockType.PROMOTION]: StoreBodyBlock.specialOffers,
  [EBlockType.ROLLING_OFFER]: StoreBodyBlock.rollingOffers,
  [EBlockType.ONE_PLUS_MANY]: StoreBodyBlock.onePlusMany,
  [EBlockType.DAILY_BONUS]: StoreBodyBlock.dailyBonusBadge,
  [EBlockType.REWARD_CALENDAR]: StoreBodyBlock.rewardCalendar,
  [EBlockType.TRIGGERED_POPUP]: StoreBodyBlock.triggeredPopups,
  [EBlockType.PROGRESS_BAR]: StoreBodyBlock.progressBars,
  [EBlockType.RICH_TEXT]: StoreBodyBlock.richText,
  [EBlockType.BANNER]: StoreBodyBlock.banners,
  [EBlockType.PLAYER_LEVEL]: StoreBodyBlock.playerLevel
};

const STORE_BODY_BLOCK_TO_BUILDER: Partial<
  Record<StoreBodyBlock, BlockBuilder>
> = {
  [StoreBodyBlock.bundles]: {
    type: EBlockType.BUNDLE,
    build: (values) => {
      const source = values.bundle;
      const fields: BlockFields = {};
      assignIfDefined(fields, 'selectedDesignId', source?.selectedDesignId);
      assignIfDefined(fields, 'themes', source?.themes);
      if (source?.offersPerRow != null) {
        fields.offersPerRow = Number(source.offersPerRow);
      }
      assignIfDefined(fields, 'bundleViewModel', source?.bundleViewModel);
      return fields;
    }
  },
  [StoreBodyBlock.specialOffers]: {
    type: EBlockType.PROMOTION,
    build: (values) => {
      const fields: BlockFields = {};
      assignIfDefined(fields, 'selectedDesignId', values.promotion?.selectedDesignId);
      assignIfDefined(fields, 'themes', values.promotion?.themes);
      return fields;
    }
  },
  [StoreBodyBlock.rollingOffers]: {
    type: EBlockType.ROLLING_OFFER,
    build: (values) => {
      const source = values.rollingOffers;
      const fields: BlockFields = {};
      assignIfDefined(fields, 'selectedDesignId', source?.selectedDesignId);
      assignIfDefined(fields, 'themes', source?.themes);
      assignIfDefined(fields, 'desktopLayout', source?.desktopLayout);
      return fields;
    }
  },
  [StoreBodyBlock.dailyBonusBadge]: {
    type: EBlockType.DAILY_BONUS,
    build: (values) => {
      const fields: BlockFields = {};
      assignIfDefined(fields, 'selectedDesignId', values.popup?.selectedDesignId);
      assignIfDefined(
        fields,
        'selectedBadgeDesignId',
        values.dailyBonusBadge?.selectedDesignId
      );
      assignIfDefined(fields, 'badgeThemes', values.dailyBonusBadge?.themes);
      return fields;
    }
  },
  [StoreBodyBlock.rewardCalendar]: {
    type: EBlockType.REWARD_CALENDAR,
    build: (values) => {
      const source = values.rewardCalendar;
      const fields: BlockFields = {};
      assignIfDefined(fields, 'selectedDesignId', source?.selectedDesignId);
      assignIfDefined(fields, 'themes', source?.themes);
      assignIfDefined(
        fields,
        'selectedBadgeDesignId',
        source?.selectedBadgeDesignId
      );
      return fields;
    }
  },
  [StoreBodyBlock.triggeredPopups]: {
    type: EBlockType.TRIGGERED_POPUP,
    build: (values) => {
      const source = values.triggeredPopups;
      const fields: BlockFields = {};
      assignIfDefined(fields, 'selectedDesignId', source?.selectedDesignId);
      assignIfDefined(fields, 'themes', source?.themes);
      assignIfDefined(
        fields,
        'selectedBadgeDesignId',
        source?.selectedBadgeDesignId
      );
      return fields;
    }
  },
  [StoreBodyBlock.progressBars]: {
    type: EBlockType.PROGRESS_BAR,
    build: (values) => {
      const fields: BlockFields = {};
      assignIfDefined(fields, 'selectedDesignId', values.progressBar?.selectedDesignId);
      return fields;
    }
  },
  [StoreBodyBlock.richText]: {
    type: EBlockType.RICH_TEXT,
    build: (values) => {
      const processed = processRichTextContent(values.richText);
      const fields: BlockFields = {};
      assignIfDefined(fields, 'text', processed?.text);
      assignIfDefined(fields, 'fontColor', processed?.fontColor);
      return fields;
    }
  },
  [StoreBodyBlock.banners]: {
    type: EBlockType.BANNER,
    build: () => ({})
  },
  [StoreBodyBlock.playerLevel]: {
    type: EBlockType.PLAYER_LEVEL,
    build: () => ({})
  }
};

const assignIfDefined = (
  fields: BlockFields,
  key: string,
  value: unknown
): void => {
  if (value !== undefined && value !== null) {
    fields[key] = value;
  }
};

const isOnePlusManyFormBlock = (
  block: IFormStoreBlock
): block is IOnePlusManyFormStoreBlock => block.type === EBlockType.ONE_PLUS_MANY;

export const isRemovableStoreBlock = (
  storeBlocks: IFormStoreBlock[],
  blockId: string
): boolean => {
  const target = storeBlocks.find((block) => block.id === blockId);
  if (target === undefined) return false;
  if (!isOnePlusManyFormBlock(target)) return true;
  return storeBlocks.filter(isOnePlusManyFormBlock).length > 1;
};

const coerceRewardsLayoutCount = (value: unknown): 1 | 2 | 3 => {
  if (value === 1 || value === 2 || value === 3) {
    return value;
  }
  return parseFreeTilesLayout(value as FreeTilesLayout | undefined);
};

const toOnePlusManyInstanceConfig = (
  block: IOnePlusManyStoreBlock
): IOnePlusManyInstanceConfig => {
  let maxOffersValue = DEFAULT_ONE_PLUS_MANY_MAX_OFFERS;
  if (typeof block.maxOffers.value === 'number') {
    maxOffersValue = block.maxOffers.value;
  }
  return {
    desktopLayout: block.desktopLayout,
    themes: (block.themes || []) as EThemes[],
    selectedDesignId: block.selectedDesignId || '',
    maxOffers: {
      unlimited: block.maxOffers.unlimited,
      value: maxOffersValue
    },
    rewardsLayout: coerceRewardsLayoutCount(block.rewardsLayout)
  };
};

const toOnePlusManyFormStoreBlock = (
  block: IOnePlusManyStoreBlock,
  fallbackSuffix: number
): IOnePlusManyFormStoreBlock => {
  let instanceKey = parseOnePlusManyInstanceKeyFromWireId(block.id);
  if (instanceKey === undefined) {
    instanceKey = buildOnePlusManyInstanceId(fallbackSuffix);
  }
  return {
    id: block.id,
    instanceKey,
    type: EBlockType.ONE_PLUS_MANY,
    isVisible: block.isVisible,
    mergedTo: block.mergedTo,
    ...toOnePlusManyInstanceConfig(block)
  };
};

const toFormStoreBlockStub = (block: TStoreBlock): IFormStoreBlockStub => ({
  id: block.id,
  type: block.type,
  isVisible: block.isVisible,
  mergedTo: block.mergedTo
});

const resolveStoreBodyBlockFromFormBlock = (
  block: IFormStoreBlock
): StoreBodyBlock | undefined => E_BLOCK_TYPE_TO_STORE_BODY_BLOCK[block.type];

const ensureStoreBlockOfType = (
  storeBlocks: IFormStoreBlock[],
  blockType: EBlockType,
  defaultIsVisible: boolean = true
): IFormStoreBlock[] => {
  const targetStoreBodyBlock = E_BLOCK_TYPE_TO_STORE_BODY_BLOCK[blockType];
  if (targetStoreBodyBlock === undefined) return storeBlocks;

  const hasBlock = storeBlocks.some(
    (block) =>
      resolveStoreBodyBlockFromFormBlock(block) === targetStoreBodyBlock
  );
  if (hasBlock) return storeBlocks;

  const newBlock: IFormStoreBlockStub = {
    id: `bb-${String(targetStoreBodyBlock)}-${v4()}`,
    type: blockType,
    isVisible: defaultIsVisible
  };

  const precedingBlocks = new Set(
    DEFAULT_BODY_BLOCKS_ORDER.slice(
      0,
      DEFAULT_BODY_BLOCKS_ORDER.indexOf(targetStoreBodyBlock)
    )
  );
  const insertIndex = storeBlocks.reduce((acc, block, index) => {
    const storeBodyBlock = resolveStoreBodyBlockFromFormBlock(block);
    if (storeBodyBlock !== undefined && precedingBlocks.has(storeBodyBlock)) {
      return index + 1;
    }
    return acc;
  }, 0);

  const next = [...storeBlocks];
  next.splice(insertIndex, 0, newBlock);
  return next;
};

export const ensureBannerStoreBlock = (
  storeBlocks: IFormStoreBlock[]
): IFormStoreBlock[] => ensureStoreBlockOfType(storeBlocks, EBlockType.BANNER);

export const ensurePlayerLevelStoreBlock = (
  storeBlocks: IFormStoreBlock[]
): IFormStoreBlock[] =>
  ensureStoreBlockOfType(storeBlocks, EBlockType.PLAYER_LEVEL, false);

export const hydrateFormStoreBlocksFromWire = (
  wireStoreBlocks: TStoreBlock[] | undefined
): IFormStoreBlock[] => {
  if (!wireStoreBlocks || wireStoreBlocks.length === 0) {
    return [];
  }
  return wireStoreBlocks.map((block, index) => {
    if (block.type === EBlockType.ONE_PLUS_MANY) {
      return toOnePlusManyFormStoreBlock(
        block as IOnePlusManyStoreBlock,
        index + 1
      );
    }
    return toFormStoreBlockStub(block);
  });
};

const buildOnePlusManyWireBlock = (
  instance: IOnePlusManyFormStoreBlock
): IOnePlusManyStoreBlock => ({
  id: instance.id,
  type: EBlockType.ONE_PLUS_MANY,
  isVisible: instance.isVisible,
  mergedTo: instance.mergedTo,
  desktopLayout: instance.desktopLayout,
  themes: instance.themes,
  selectedDesignId: instance.selectedDesignId,
  maxOffers: {
    unlimited: instance.maxOffers.unlimited,
    value: instance.maxOffers.value
  },
  rewardsLayout: formatFreeTilesLayout(instance.rewardsLayout)
});

const buildWireBlockFromStub = (
  stub: IFormStoreBlockStub,
  values: TemplateStudioFormValues
): TStoreBlock | undefined => {
  const storeBodyBlock = E_BLOCK_TYPE_TO_STORE_BODY_BLOCK[stub.type];
  if (storeBodyBlock === undefined) return undefined;
  const builder = STORE_BODY_BLOCK_TO_BUILDER[storeBodyBlock];
  if (!builder) return undefined;
  return {
    id: stub.id,
    type: builder.type,
    isVisible: stub.isVisible,
    mergedTo: stub.mergedTo,
    ...builder.build(values)
  } as TStoreBlock;
};

export const buildStoreBlocksFromForm = (
  values: TemplateStudioFormValues
): TStoreBlock[] => {
  const formStoreBlocks = (values as { storeBlocks?: IFormStoreBlock[] })
    .storeBlocks;
  if (!Array.isArray(formStoreBlocks) || formStoreBlocks.length === 0) {
    return [];
  }

  const wireBlocks: TStoreBlock[] = [];
  for (const entry of formStoreBlocks) {
    if (isOnePlusManyFormBlock(entry)) {
      wireBlocks.push(buildOnePlusManyWireBlock(entry));
      continue;
    }
    const wireBlock = buildWireBlockFromStub(entry, values);
    if (wireBlock !== undefined) {
      wireBlocks.push(wireBlock);
    }
  }
  return wireBlocks;
};

export const buildPreviewStoreBlocks = (
  values: TemplateStudioFormValues
): TStoreBlock[] => buildStoreBlocksFromForm(values);

export const deriveBodyBlocksOrderV2FromStoreBlocks = (
  storeBlocks: IFormStoreBlock[]
): BodyBlockV2Item[] => {
  const items: BodyBlockV2Item[] = [];
  let index = 0;

  while (index < storeBlocks.length) {
    const block = storeBlocks[index];
    if (block.type === EBlockType.ONE_PLUS_MANY) {
      items.push({
        id: `bb-${StoreBodyBlock.onePlusMany}`,
        blocks: [StoreBodyBlock.onePlusMany],
        isVisible: block.isVisible,
        isMergeable: false
      });
      index++;
      continue;
    }

    const storeBodyBlock = E_BLOCK_TYPE_TO_STORE_BODY_BLOCK[block.type];
    if (storeBodyBlock === undefined) {
      index++;
      continue;
    }

    const groupBlocks: StoreBodyBlock[] = [storeBodyBlock];
    let nextIndex = index + 1;
    while (nextIndex < storeBlocks.length) {
      const nextBlock = storeBlocks[nextIndex];
      if (nextBlock.type === EBlockType.ONE_PLUS_MANY) break;
      const includesNext = block.mergedTo?.includes(nextBlock.id) === true;
      if (!includesNext) break;
      const nextStoreBodyBlock =
        E_BLOCK_TYPE_TO_STORE_BODY_BLOCK[nextBlock.type];
      if (nextStoreBodyBlock === undefined) break;
      groupBlocks.push(nextStoreBodyBlock);
      nextIndex++;
    }
    if (groupBlocks.length > 1) {
      items.push({
        id: `bb-${groupBlocks.map(String).join('+')}`,
        blocks: groupBlocks as BodyBlockId[],
        isVisible: block.isVisible,
        isMergeable: true
      });
      index = nextIndex;
      continue;
    }

    items.push({
      id: `bb-${String(storeBodyBlock)}`,
      blocks: [storeBodyBlock as BodyBlockId],
      isVisible: block.isVisible,
      isMergeable: MERGEABLE_BODY_BLOCKS.has(storeBodyBlock)
    });
    index++;
  }

  return collapseOnePlusManyToSingleSlot(items);
};

const linkMergedRowEntries = (rowEntries: IFormStoreBlock[]): void => {
  if (rowEntries.length <= 1) return;
  const [first, ...rest] = rowEntries;
  rowEntries[0] = {
    ...first,
    mergedTo: rest.map((entry) => entry.id)
  };
};

export const buildFormStoreBlocksFromBodyBlocksV2 = (
  bodyBlocksOrderV2: BodyBlockV2Item[],
  buildDefaultOnePlusManyInstance: BuildDefaultOnePlusManyInstance
): IFormStoreBlock[] => {
  const result: IFormStoreBlock[] = [];
  let nextOnePlusManySuffix = FIRST_INSTANCE_SUFFIX;

  for (const item of bodyBlocksOrderV2) {
    const rowEntries: IFormStoreBlock[] = [];
    for (const block of item.blocks) {
      const baseBlock = getBaseBlockType(block);
      if (baseBlock === StoreBodyBlock.onePlusMany) {
        const instance = buildDefaultOnePlusManyInstance(
          buildOnePlusManyInstanceId(nextOnePlusManySuffix)
        );
        nextOnePlusManySuffix += 1;
        rowEntries.push({ ...instance, isVisible: item.isVisible });
        continue;
      }
      const builder = STORE_BODY_BLOCK_TO_BUILDER[baseBlock];
      if (!builder) continue;
      rowEntries.push({
        id: `bb-${String(baseBlock)}-${v4()}`,
        type: builder.type,
        isVisible: item.isVisible
      });
    }

    linkMergedRowEntries(rowEntries);
    result.push(...rowEntries);
  }

  return result;
};
